import {
  type ConversionTrackingEventInput,
  type TrackConversionEventParams,
  type TrackCompletedAttemptMoneyEvent,
} from "@circle-react/api/conversionTrackingApi";
import { conversionTrackingApi } from "@circle-react/api/conversionTrackingApi";
import { buildMetaFunnelEvent } from "@circle-react/helpers/metaFunnelEventHelpers";
import {
  shouldTrackMetaCompletedAttempt,
  trackMetaEvent,
} from "@circle-react/helpers/metaPixel";
import { useCallback, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

interface UseConversionTrackingEventTriggerParams {
  hasEnabledProvider: boolean;
  isCapturePending: boolean;
  isConsentGranted: boolean;
  isReady: boolean;
  isSettingsPending: boolean;
  metaPixelId?: string;
}

const MAX_PENDING_EVENTS = 20;

const isCompletedAttemptMoneyEvent = (
  event: ConversionTrackingEventInput,
): event is TrackCompletedAttemptMoneyEvent =>
  event.eventName === "purchase" ||
  event.eventName === "subscribe" ||
  event.eventName === "start_trial";

export const useConversionTrackingEventTrigger = ({
  hasEnabledProvider,
  isCapturePending,
  isConsentGranted,
  isSettingsPending,
  isReady,
  metaPixelId,
}: UseConversionTrackingEventTriggerParams) => {
  const lastFiredEventKeyRef = useRef<string | null>(null);
  const pendingEventsRef = useRef<TrackConversionEventParams[]>([]);
  const pendingEventKeysRef = useRef(new Set<string>());

  const dispatchFunnelEvent = useCallback(
    (event: TrackConversionEventParams) => {
      const funnelUuid = event.funnelUuid?.trim();
      const eventKey = funnelUuid
        ? [
            funnelUuid,
            event.eventName,
            event.checkoutSessionId,
            event.paymentType,
          ]
            .filter(Boolean)
            .join(":")
        : undefined;

      if (eventKey && lastFiredEventKeyRef.current === eventKey) return;
      if (eventKey) lastFiredEventKeyRef.current = eventKey;

      const eventParams = funnelUuid
        ? { ...event, funnelUuid }
        : {
            ...event,
            funnelUuid: undefined,
            fireNonce: event.fireNonce ?? uuidv4(),
          };

      void conversionTrackingApi.track(eventParams).catch((error: unknown) => {
        if (process.env.NODE_ENV !== "production") {
          console.warn("ConversionTrackingContext failed to send event", error);
        }
      });

      if (metaPixelId) {
        trackMetaEvent(buildMetaFunnelEvent(eventParams, metaPixelId));
      }
    },
    [metaPixelId],
  );
  const flushPendingEvents = useCallback(() => {
    const pendingEvents = pendingEventsRef.current;
    pendingEventsRef.current = [];
    pendingEventKeysRef.current.clear();
    pendingEvents.forEach(dispatchFunnelEvent);
  }, [dispatchFunnelEvent]);
  const queuePendingFunnelEvent = useCallback(
    (event: TrackConversionEventParams) => {
      const funnelUuid = event.funnelUuid?.trim();
      const eventKey = funnelUuid
        ? [
            funnelUuid,
            event.eventName,
            event.checkoutSessionId,
            event.paymentType,
          ]
            .filter(Boolean)
            .join(":")
        : undefined;

      if (eventKey && pendingEventKeysRef.current.has(eventKey)) return true;
      if (pendingEventsRef.current.length >= MAX_PENDING_EVENTS) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("ConversionTrackingContext pending event queue is full");
        }
        return false;
      }

      const eventParams = funnelUuid
        ? { ...event, funnelUuid }
        : {
            ...event,
            funnelUuid: undefined,
            fireNonce: event.fireNonce ?? uuidv4(),
          };
      pendingEventsRef.current.push(eventParams);
      if (eventKey) pendingEventKeysRef.current.add(eventKey);
      return true;
    },
    [],
  );

  useEffect(() => {
    if (!isConsentGranted || (!hasEnabledProvider && !isSettingsPending)) {
      pendingEventsRef.current = [];
      pendingEventKeysRef.current.clear();
      return;
    }

    if (isReady) {
      flushPendingEvents();
      return;
    }

    if (
      !isCapturePending &&
      !isSettingsPending &&
      pendingEventsRef.current.length > 0
    ) {
      pendingEventsRef.current = [];
      pendingEventKeysRef.current.clear();
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "ConversionTrackingContext discarded pending events because attribution was unavailable",
        );
      }
    }
  }, [
    dispatchFunnelEvent,
    flushPendingEvents,
    hasEnabledProvider,
    isCapturePending,
    isConsentGranted,
    isSettingsPending,
    isReady,
  ]);

  return useCallback(
    (event: ConversionTrackingEventInput): boolean => {
      if (isCompletedAttemptMoneyEvent(event)) {
        const shouldTrack =
          !!event.attemptId &&
          shouldTrackMetaCompletedAttempt(event.attemptStatus);
        if (!shouldTrack) return false;

        trackMetaEvent({
          eventName: event.eventName,
          dedupKey: String(event.attemptId),
          pixelId: metaPixelId,
          value: event.value,
          currency: event.currency,
        });
        return true;
      }

      if (!isConsentGranted) return false;
      if (!hasEnabledProvider && !isSettingsPending) return false;
      if (isReady) {
        flushPendingEvents();
        dispatchFunnelEvent(event);
        return true;
      }
      if (isCapturePending || isSettingsPending) {
        return queuePendingFunnelEvent(event);
      }
      return false;
    },
    [
      dispatchFunnelEvent,
      flushPendingEvents,
      hasEnabledProvider,
      isCapturePending,
      isConsentGranted,
      queuePendingFunnelEvent,
      isSettingsPending,
      isReady,
    ],
  );
};
